import React, { useState, useEffect, useCallback } from 'react';
import { Coins, AlertCircle, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Compact AI-credits badge for the top nav.
 *
 * - Shows current balance + ":beta" indicator if pulling from owner pool
 * - Goes red when below $0.50 (low-balance warning)
 * - Click → opens the top-up modal
 * - Auto-refreshes on `wallet:refresh` window events emitted by ClaudeFAB
 *   after every AI message
 */
export default function WalletBalanceBadge({ onClickTopUp }) {
  const [balance, setBalance] = useState(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { setBalance(null); return; }
      const r = await fetch(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { setBalance(null); return; }
      setBalance(await r.json());
    } catch {
      setBalance(null);
    }
  }, []);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('wallet:refresh', onRefresh);
    return () => window.removeEventListener('wallet:refresh', onRefresh);
  }, [load]);

  if (!balance) return null;

  const isPool = balance.billing_mode === 'owner_pool';
  const usd = isPool ? balance.pool_balance_usd : balance.balance_usd;
  const isLow = !isPool && usd < 0.50;
  const isZero = !isPool && usd <= 0;

  return (
    <button
      type="button"
      onClick={onClickTopUp}
      title={
        isPool
          ? `Beta access — pulling from owner pool ($${usd?.toFixed?.(2) ?? '0.00'} remaining). No personal charges.`
          : `AI Copilot credits: $${usd?.toFixed?.(4) ?? '0.0000'}. Click to top up.`
      }
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-['JetBrains_Mono'] text-xs uppercase tracking-wider transition-colors border ${
        isZero
          ? 'bg-red-500/20 border-red-500/40 text-red-300 hover:bg-red-500/30'
          : isLow
          ? 'bg-amber-500/20 border-amber-500/40 text-amber-300 hover:bg-amber-500/30'
          : isPool
          ? 'bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25'
          : 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25'
      }`}
      data-testid="wallet-balance-badge"
    >
      {isZero ? <AlertCircle className="w-3.5 h-3.5" /> : <Coins className="w-3.5 h-3.5" />}
      <span>
        {isPool ? 'beta · ' : ''}
        ${typeof usd === 'number' ? usd.toFixed(usd >= 1 ? 2 : 4) : '0.00'}
      </span>
      {!isPool && <Plus className="w-3 h-3 opacity-70" />}
    </button>
  );
}
